import StepProgressBar, {Step} from "./StepProgressBar.tsx";
import {useCallback, useEffect, useRef, useState} from "react";
import {StepOne, StepOneFormData} from "./OnboardingFormStepOne.tsx";
import {StepTwo, StepTwoFormData} from "./OnboardingFormStepTwo.tsx";
import {StepThree} from "./OnboardingFormStepThree.tsx";

import validator from 'validator';
import {UnauthenticatedBackendApi} from "../../api/UnauthenticatedApi.ts";
import {BackendApi} from "../../api/api.ts";
import {Validator, validatorChain} from "../../components/form/validation.ts";
import {
    businessRegistrationValidators,
    BusinessRegistrationFormData,
} from "../../lib/businessRegistration.ts";
import {FormTodoType} from "../../components/form/FormTodo.tsx";
import {FormError} from "../../components/form/FormError.tsx";
import {Logo} from "../../components/specific/Logo.tsx";
import {useSearchParams} from "react-router-dom";
import {auth} from "../../firebaseApp.ts";
import {SocialSignInButtons} from "../../components/auth/SocialSignInButtons.tsx";
import {getProfileFromFirebaseUser, isFromSsoProvider} from "../../lib/ssoAuth.ts";


export const accountValidators: Array<Validator<StepOneFormData>> = [
    {
        fieldName: 'email',
        validationFunction: (data: StepOneFormData) => {
            return validator.isEmail(data.email)
        },
        message: 'Geen geldig e-mailadres'
    },
    {
        fieldName: 'phone_number',
        validationFunction: (data: StepOneFormData) => {
            return validator.isMobilePhone(data.phone_number.replace(/\s/g, ''), 'any', {
                strictMode: true
            })
        },
        message: 'Geen geldig telefoonnummer'
    },
    {
        fieldName: 'first_name',
        validationFunction: (data: StepOneFormData) => {
            return validator.isLength(data.first_name, {min: 1, max: 255})
        },
        message: 'Voornaam moet tussen 1 en 255 tekens zijn'
    },
    {
        fieldName: 'last_name',
        validationFunction: (data: StepOneFormData) => {
            return validator.isLength(data.last_name, {min: 1, max: 255})
        },
        message: 'Achternaam moet tussen 1 en 255 tekens zijn'
    },
    {
        fieldName: 'password',
        validationFunction: (data: StepOneFormData) => {
            return validator.isLength(data.password, {min: 8, max: 255})
        },
        message: 'Wachtwoord moet tussen 8 en 255 tekens zijn'
    },
    {
        fieldName: 'password',
        validationFunction: (data: StepOneFormData) => {
            return /[a-z]/.test(data.password);
        },
        message: 'Wachtwoord moet minstens één kleine letter bevatten'
    },
    {
        fieldName: 'password',
        validationFunction: (data: StepOneFormData) => {
            return /[A-Z]/.test(data.password);
        },
        message: 'Wachtwoord moet minstens één hoofdletter bevatten'
    },
    {
        fieldName: 'password',
        validationFunction: (data: StepOneFormData) => {
            return /[0-9]/.test(data.password);
        },
        message: 'Wachtwoord moet minstens één cijfer bevatten'
    },
    {
        fieldName: 'password',
        validationFunction: (data: StepOneFormData) => {
            const specificSpecialCharsRegex = /[\!"#\$%\&'\(\)\*\+,\-\.\/\:\;\<\=\>\?\@\[\\\]\^\_\`\{\|\}\~]/;
            return specificSpecialCharsRegex.test(data.password);
        },
        message: 'Wachtwoord moet minstens één speciaal teken bevatten'
    },
    {
        fieldName: 'repeat_password',
        validationFunction: (data: StepOneFormData) => {
            return validator.equals(data.password, data.repeat_password)
        },
        message: 'Wachtwoorden komen niet overeen'
    },
    {
        fieldName: 'repeat_password',
        validationFunction: (data: StepOneFormData) => {
            return validator.isLength(data.repeat_password, {min: 8, max: 255})
        },
        message: 'Herhaal wachtwoord moet tussen 8 en 255 tekens zijn'
    },
]

const ONBOARDING_FIELD_BY_REGISTRATION_FIELD: Record<keyof BusinessRegistrationFormData, keyof StepTwoFormData> = {
    name: "business_name",
    legal_entity_type: "legal_entity_type",
    registration_number: "registration_number",
    fte_amount: "fte_amount",
    industry: "industry",
    description: "business_description",
};

function toBusinessRegistrationFormData(business: StepTwoFormData): BusinessRegistrationFormData {
    return {
        name: business.business_name,
        legal_entity_type: business.legal_entity_type,
        registration_number: business.registration_number,
        fte_amount: business.fte_amount,
        industry: business.industry,
        description: business.business_description,
    };
}

async function sendDataToBackend(
    accountData: StepOneFormData,
    businesses: StepTwoFormData[],
    isSSOMode: boolean,
    onErrorFunc: (error: string) => void
) {
    const payload = {
        first_name: accountData.first_name,
        last_name: accountData.last_name,
        email: accountData.email,
        phone_number: accountData.phone_number,
        businesses: businesses,
        app_hostname: window.location.hostname,
    };

    try {
        if (isSSOMode) {
            const api = new BackendApi();
            await api.Onboarding__createSsoUser(payload);
            window.location.href = '/';
            return;
        }

        const unauthenticatedApi = new UnauthenticatedBackendApi();
        const rawResponse = await unauthenticatedApi.post('onboarding/create-user', {
            ...accountData,
            businesses: businesses,
            app_hostname: window.location.hostname
        });

        const response = await rawResponse.json().catch(() => null);

        if (!rawResponse.ok) {
            onErrorFunc(
                response?.detail
                || response?.message
                || 'Er is een onbekende fout opgetreden op de server. We zijn hiervan op de hoogte gesteld. Probeer het later opnieuw.'
            );
            return;
        }
    } catch (e: any) {
        const message = e?.detail?.message || e?.detail || e?.message;
        onErrorFunc(message || "Er is een onbekende fout opgetreden op de server. We zijn hiervan op de hoogte gesteld. Probeer het later opnieuw.")
    }
}

export function OnboardingFormPage() {
    const [searchParams] = useSearchParams();
    const [isSSOMode, setIsSSOMode] = useState(searchParams.get('sso') === '1');

    const [steps, setSteps] = useState<Array<Step>>([
        {
            id: 1,
            name: 'Maak een account aan',
            desc: "Alle basisinformatie",
            onClick: updateCurrentStep,
            current: true
        },
        {
            id: 2,
            name: 'Vertel ons over je bedrijf',
            desc: "We gebruiken deze gegevens om het product op maat voor je te maken",
            onClick: updateCurrentStep,
            current: false
        },
        {
            id: 3,
            name: 'Afronden',
            desc: "",
            onClick: updateCurrentStep,
            current: false
        }
    ])

    const [formError, setFormError] = useState('');

    const [accountData, setAccountData] = useState<StepOneFormData>({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        repeat_password: '',
        phone_number: '',
    })

    const [businesses, setBusinesses] = useState<StepTwoFormData[]>([])

    const [formTodo, setFormTodo] = useState<Array<FormTodoType>>([])

    const formStart = useRef<HTMLDivElement | null>(null);
    const [stepOneKey, setStepOneKey] = useState(0);
    const ssoAuthPrefillAppliedRef = useRef(false);

    const applySsoProfile = useCallback((profile: ReturnType<typeof getProfileFromFirebaseUser>) => {
        setIsSSOMode(true);
        setAccountData((prev) => ({
            ...prev,
            email: profile.email || prev.email,
            first_name: profile.first_name || prev.first_name,
            last_name: profile.last_name || prev.last_name,
        }));
        setStepOneKey((currentKey) => currentKey + 1);
    }, []);

    useEffect(() => {
        return auth.onAuthStateChanged((firebaseUser) => {
            if (!firebaseUser || !isFromSsoProvider(firebaseUser)) {
                return;
            }

            if (ssoAuthPrefillAppliedRef.current) {
                return;
            }

            ssoAuthPrefillAppliedRef.current = true;
            applySsoProfile(getProfileFromFirebaseUser(firebaseUser));
        });
    }, [applySsoProfile]);

    function getAccountValidators(): Array<Validator<StepOneFormData>> {
        if (isSSOMode) {
            return accountValidators.filter((validator) =>
                !['password', 'repeat_password'].includes(validator.fieldName)
            );
        }
        return accountValidators;
    }

    function canContinue(currentStep: Step) {
        let issueFound = false
        const todos: Array<FormTodoType> = []

        if (currentStep.id === 1) {
            const validators = getAccountValidators();
            const fieldsToValidate = isSSOMode
                ? ['email', 'phone_number', 'first_name', 'last_name']
                : Object.keys(accountData);

            for (const key of fieldsToValidate) {
                const todosForThisField = validatorChain<StepOneFormData>(key, accountData, validators)

                if (todosForThisField.length > 0)
                    issueFound = true

                todos.push(...todosForThisField)
            }
        }

        if (currentStep.id === 2) {
            businesses.forEach((business, index) => {
                const registrationData = toBusinessRegistrationFormData(business);
                for (const key of Object.keys(registrationData)) {
                    const todosForThisField = validatorChain<BusinessRegistrationFormData>(
                        key,
                        registrationData,
                        businessRegistrationValidators,
                    );

                    if (todosForThisField.length > 0)
                        issueFound = true;

                    todos.push(...todosForThisField.map((todo) => ({
                        ...todo,
                        fieldName: `businesses[${index}].${ONBOARDING_FIELD_BY_REGISTRATION_FIELD[todo.fieldName as keyof BusinessRegistrationFormData]}`,
                    })));
                }
            })
        }

        setFormTodo(todos);
        if (issueFound) formStart.current?.scrollIntoView({behavior: "smooth", block: "start"})
        return !issueFound;
    }

    function updateCurrentStep(clickedStep: Step) {
        setSteps((prev: Array<Step>) => {
            const newStepsState = [];
            for (const step of prev) {
                step.current = step.id === clickedStep.id;
                newStepsState.push(step)
            }

            return newStepsState
        })
    }

    const currentStep = steps.filter((step: Step) => step.current)[0]
    const isNextDisabled = currentStep?.id === 2 && (!businesses || businesses.length === 0)

    return (
        <div
            className="min-h-screen pt-8 flex flex-col items-center justify-start mx-auto md:max-w-7xl px-4 sm:px-6 lg:px-8 pb-12">
            <header className="bg-white w-full flex justify-center mb-12">
                <Logo/>
            </header>
            {formError && <div className="mb-8"><FormError error={formError}/></div>}
            <main className="mx-auto w-full md:max-w-2xl" ref={formStart}>
                <StepProgressBar steps={steps}/>
                <div
                    className="mx-auto max-w-2xl mb-8 md:mb-32 min-h-[70vh] md:min-h-[50em] mt-16">
                    {steps.filter((step: Step) => step.current)[0].id === 1 && (
                        <>
                            {!isSSOMode && (
                                <>
                                    <SocialSignInButtons
                                        googleLabel="Registreer met Google"
                                        microsoftLabel="Registreer met Microsoft"
                                        onSuccess={() => {
                                            const firebaseUser = auth.currentUser;
                                            if (!firebaseUser) {
                                                return;
                                            }
                                            applySsoProfile(getProfileFromFirebaseUser(firebaseUser));
                                        }}
                                    />
                                    <div className="relative my-8">
                                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div className="w-full border-t border-gray-300"/>
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="bg-white px-2 text-gray-500">of</span>
                                        </div>
                                    </div>
                                </>
                            )}
                            <StepOne key={stepOneKey}
                                     onUpdateFunc={(formData: StepOneFormData) => {
                                         setAccountData(formData);
                                     }}
                                     formTodos={formTodo}
                                     initialFormData={accountData}
                                     isSSOMode={isSSOMode}
                            />
                        </>
                    )}
                    {steps.filter((step: Step) => step.current)[0].id === 2 && (
                        <StepTwo onUpdateFunc={
                            (formData: StepTwoFormData[]) => {
                                setBusinesses(formData)
                            }
                        }
                                 formTodos={formTodo} defaultBusinesses={businesses}/>
                    )}
                    {steps.filter((step: Step) => step.current)[0].id === 3 && (
                        <StepThree isSSOMode={isSSOMode}/>
                    )}

                    {steps.filter((step: Step) => step.current)[0].id !== steps[steps.length - 1].id && <div>
                        <button
                            type="submit"
                            className="flex w-full mt-8 justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary hover:opacity-70 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isNextDisabled}
                            onClick={() => {
                                const currentStep = steps.filter((step: Step) => step.current)[0]
                                const nextStep = steps.filter((step: Step) => step.id === currentStep.id + 1)[0]

                                const userCanContinue = canContinue(currentStep)

                                if (userCanContinue)
                                    updateCurrentStep(nextStep)

                                if (false) { // debug
                                    console.log(`Current step: ${currentStep.id}`)
                                    console.log(`formTodo: ${formTodo.length}`)
                                    console.log(formTodo)
                                    console.log(`userCanContinue: ${userCanContinue}`)
                                }

                                if (currentStep.id === 1 && userCanContinue) {
                                    setFormError('')
                                }

                                if (currentStep.id === 2 && userCanContinue) {
                                    setFormError('')
                                    sendDataToBackend(accountData, businesses, isSSOMode, (error) => {
                                        setFormError(error)
                                        updateCurrentStep(steps[0])
                                    })
                                }
                            }}
                        >
                            Doorgaan
                        </button>
                    </div>}
                </div>
            </main>
        </div>
    )
}
